import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ArrayByLengthPipe } from '../../../../shared/pipes/array-by-length.pipe';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../core/enums/CartItemTypes';
import { ICartItem } from '../../core/interfaces/ICartItem';
import { CartItemDisplayTotalPipe } from '../../core/pipes/cart-item-display-total.pipe';
import { CartService } from '../../core/services/CartService';
import { PaySeparatelyService } from '../../core/services/PaySeparatelyService';
import { CartStore } from '../../core/stores/CartStore';
import { PaySeparatelyStore } from '../../core/stores/PaySeparatelyStore';

@Component({
  selector: 'app-pay-separately-selection-modal',
  standalone: true,
  imports: [
    ModalComponent,
    NgTemplateOutlet,
    NumberToCurrencyPipe,
    TranslatePipe,
    ArrayByLengthPipe,
    LocaleDatetimeFormatPipe,
    ChipComponent,
    CartItemDisplayTotalPipe
  ],
  templateUrl: './pay-separately-selection-modal.component.html',
  styleUrl: './pay-separately-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaySeparatelySelectionModalComponent extends BaseModalComponent {

  public confirmInProgress = false;
  public readonly cartItemTypes = CartItemTypes;
  public readonly paySeparatelyStore = inject(PaySeparatelyStore);
  public readonly cartStore = inject(CartStore);
  private _hiddenPositions: string[] = [];
  private readonly _paySeparatelyService = inject(PaySeparatelyService);
  private readonly _cartService = inject(CartService);

  public get areAllItemsSelected(): boolean {
    const cart = this.cartStore.cart();

    for (const item of cart.items) {
      if (item.itemType === CartItemTypes.Product || item.itemType === CartItemTypes.Package || (item.itemType === CartItemTypes.Voucher && item.quantity > 1)) {
        for (let subIndex = 0; subIndex < item.quantity; subIndex++) {
          if (this.paySeparatelyStore.isHidden(item.itemId, item.position, subIndex)) {
            return false;
          }
        }
      } else {
        if (this.paySeparatelyStore.isHidden(item.itemId, item.position)) {
          return false;
        }
      }
    }

    return true;
  }

  public ngOnInit(): void {
    this._paySeparatelyService.initFromStorage();
  }

  public override open(): void {
    super.open();
    this._paySeparatelyService.initFromStorage();
    this._hiddenPositions = this.paySeparatelyStore.hiddenPositions;
    this.cdr.markForCheck();
  }

  public toggleAll(): void {
    if (!this.paySeparatelyStore || !this.cartStore.cart()) {
      return;
    }

    if (this.areAllItemsSelected) {
      this.paySeparatelyStore.setNone();
    } else {
      this.paySeparatelyStore.setAll();
    }
    this.cdr.markForCheck();
  }

  public togglePositionState(item: ICartItem, value: boolean, subIndex?: number): void {
    this.paySeparatelyStore.togglePositionState(item, value, subIndex);
    this.cdr.markForCheck();
  }

  public isPositionSelected(itemId: number, position: number, subIndex?: number): boolean {
    return !this.paySeparatelyStore.isHidden(itemId, position, subIndex);
  }

  public getPackageProductsLabel(item: ICartItem): string {
    return item.packageProducts?.map(p => p.name).join(', ') ?? '';
  }

  public hasSelection(): boolean {
    if (!this.paySeparatelyStore || !this.cartStore.cart()) {
      return false;
    }

    return this.cartStore.cart().items.some(item => {
      if (item.itemType === CartItemTypes.Product || item.itemType === CartItemTypes.Package) {
        for (let subIndex = 0; subIndex < item.quantity; subIndex++) {
          if (!this.paySeparatelyStore.isHidden(item.itemId, item.position, subIndex)) {
            return true;
          }
        }
        return false;
      }
      if (item.itemType === CartItemTypes.Voucher && item.quantity > 1) {
        for (let subIndex = 0; subIndex < item.quantity; subIndex++) {
          if (!this.paySeparatelyStore.isHidden(item.itemId, item.position, subIndex)) {
            return true;
          }
        }
        return false;
      }
      return !this.paySeparatelyStore.isHidden(item.itemId, item.position);
    });
  }

  public cancel(): void {
    if (this._hiddenPositions.length > 0) {
      this._paySeparatelyService.enablePaySeparatelyMode();
      this.paySeparatelyStore.setAll(this._hiddenPositions);
    }

    super.close();
  }

  public confirm(): void {
    if (this.confirmInProgress) return;

    const hasCineCardInCart = this.cartStore.cart()?.items.some(
      item => item.itemType === CartItemTypes.CashCineCard
    ) ?? false;

    if (!hasCineCardInCart) {
      this.close();
      return;
    }

    this.confirmInProgress = true;
    this.cdr.markForCheck();

    this._cartService.recalculateCineCardAmount().subscribe({
      next: () => {
        this.confirmInProgress = false;
        this.cdr.markForCheck();
        this.close();
      },
      error: () => {
        this.confirmInProgress = false;
        this.cdr.markForCheck();
      }
    });
  }
}
